import { StatusEnum, type GetAllClientContactQuery } from '@codezo/graphql';
import { Mail, MoreHorizontal, Pencil, Phone, Send, Trash2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { InitialsAvatar } from '../ui/initials-avatar';
import { SortableTableHead } from '../ui/sortable-table-head';
import { StatusBadge } from '../ui/status-badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { clientContactStatus } from '../../lib/status-configs';
import { formatCalendarDate, formatDate } from '../../lib/utils';

export type ClientContact = NonNullable<
	GetAllClientContactQuery['getAllClientContact']['data']
>[number];

export const contactFullName = (c: ClientContact) => `${c.firstName} ${c.lastName}`.trim();

/** Single badge for the (status, isGuest, invitedAt) triple — see `clientContactStatus`. */
export function ContactStatusBadge({ contact }: { contact: ClientContact }) {
	const config = clientContactStatus(contact.status, contact.isGuest, contact.invitedAt);
	return <StatusBadge active activeLabel={config.label} activeTone={config.tone} />;
}

interface ContactsTableProps {
	contacts: ClientContact[];
	/** Hidden when the list is already scoped to one client — every row would repeat it. */
	showClient?: boolean;
	sortField: string;
	sortDir: 'asc' | 'desc';
	onSort: (field: string) => void;
	/** Selection is opt-in: pass these to get the checkbox column. */
	selectedIds?: Set<string>;
	onToggleOne?: (id: string) => void;
	onToggleAll?: () => void;
	inviting?: boolean;
	onInvite: (contact: ClientContact) => void;
	onEdit: (contact: ClientContact) => void;
	onDelete: (contact: ClientContact) => void;
}

/**
 * The client-contact table, shared by the global Contacts page and a single
 * client's Contacts tab. The two differ only in whether the Client column is
 * worth showing, so it is one component rather than two that drift apart.
 */
export function ContactsTable({
	contacts,
	showClient = true,
	sortField,
	sortDir,
	onSort,
	selectedIds,
	onToggleOne,
	onToggleAll,
	inviting = false,
	onInvite,
	onEdit,
	onDelete,
}: ContactsTableProps) {
	const selectable = !!selectedIds && !!onToggleOne && !!onToggleAll;
	const ids = contacts.map((c) => c.id);
	const allSelected = !!selectedIds && ids.length > 0 && ids.every((id) => selectedIds.has(id));
	const someSelected = !!selectedIds && ids.some((id) => selectedIds.has(id));

	const headClass = 'text-xs font-medium uppercase tracking-[0.06em] text-text-4';
	const minWidth = showClient ? 'min-w-[1300px]' : 'min-w-[1100px]';

	return (
		<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
			<div className="overflow-x-auto">
				<Table className={minWidth}>
					<TableHeader>
						<TableRow className="border-line-1 hover:bg-transparent">
							{selectable && (
								<TableHead className="w-10">
									<Checkbox
										checked={allSelected}
										indeterminate={someSelected && !allSelected}
										onCheckedChange={onToggleAll}
									/>
								</TableHead>
							)}
							<SortableTableHead
								sortKey="firstName"
								activeKey={sortField}
								direction={sortDir}
								onSort={onSort}
								className={headClass}
							>
								Contact
							</SortableTableHead>
							{/* Not sortable: email lives on the joined authentication
							    user, which the list API can't order by. */}
							<TableHead className={headClass}>Email</TableHead>
							{showClient && <TableHead className={headClass}>Client</TableHead>}
							<TableHead className={headClass}>Phone</TableHead>
							{/*
							 * Not sortable: 'YYYY-MM-DD' sorts by age, which reads as
							 * "upcoming birthdays" and isn't. That needs a month/day
							 * sort the API doesn't offer.
							 */}
							<TableHead className={headClass}>Birthday</TableHead>
							<SortableTableHead
								sortKey="status"
								activeKey={sortField}
								direction={sortDir}
								onSort={onSort}
								className={headClass}
							>
								Status
							</SortableTableHead>
							<SortableTableHead
								sortKey="createdAt"
								activeKey={sortField}
								direction={sortDir}
								onSort={onSort}
								className={headClass}
							>
								Added
							</SortableTableHead>
							<TableHead className="w-10" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{contacts.map((c) => (
							<TableRow
								key={c.id}
								data-state={selectedIds?.has(c.id) ? 'selected' : undefined}
								className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
								onClick={() => onEdit(c)}
							>
								{selectable && (
									<TableCell>
										<div onClick={(e) => e.stopPropagation()}>
											<Checkbox
												checked={selectedIds.has(c.id)}
												onCheckedChange={() => onToggleOne(c.id)}
											/>
										</div>
									</TableCell>
								)}
								<TableCell>
									<div className="flex items-center gap-3">
										<InitialsAvatar
											name={contactFullName(c)}
											src={c.avatar}
											size={32}
											shape="rounded"
										/>
										<div className="min-w-0">
											<div className="truncate text-base font-medium text-text-1">
												{contactFullName(c)}
											</div>
											{c.jobPosition && (
												<div className="truncate text-sm text-text-3">
													{c.jobPosition}
												</div>
											)}
										</div>
									</div>
								</TableCell>
								<TableCell className="text-base text-text-2">
									{c.authenticationUser?.email ? (
										<span className="flex items-center gap-1">
											<Mail size={11} className="shrink-0 text-text-4" />
											<span className="truncate">
												{c.authenticationUser.email}
											</span>
										</span>
									) : (
										'—'
									)}
								</TableCell>
								{showClient && (
									<TableCell>
										<div className="flex items-center gap-2">
											{c.organization?.logo ? (
												<img
													src={c.organization.logo}
													alt={c.organization.name}
													className="h-5 w-5 shrink-0 rounded-[var(--r-2)] border border-line-1 object-cover"
												/>
											) : (
												<InitialsAvatar
													name={c.organization?.name || '—'}
													size={25}
													shape="rounded"
													maxInitials={3}
												/>
											)}
											<span className="truncate text-base text-text-2">
												{c.organization?.name || '—'}
											</span>
										</div>
									</TableCell>
								)}
								<TableCell className="text-base text-text-2">
									{c.phone ? (
										<span className="flex items-center gap-1">
											<Phone size={11} className="shrink-0 text-text-4" />
											{c.phone}
										</span>
									) : (
										'—'
									)}
								</TableCell>
								<TableCell className="text-base text-text-2">
									{formatCalendarDate(c.birthday) || '—'}
								</TableCell>
								<TableCell>
									<ContactStatusBadge contact={c} />
								</TableCell>
								<TableCell className="text-base text-text-3">
									{c.createdAt ? formatDate(c.createdAt) : '—'}
								</TableCell>
								<TableCell>
									<div className="flex justify-end">
										<DropdownMenu>
											<DropdownMenuTrigger
												onClick={(e) => e.stopPropagation()}
												className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
											>
												<MoreHorizontal size={14} />
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												{/*
												 * Shown disabled rather than hidden for active
												 * contacts, so the action is discoverable and
												 * its unavailability is explained rather than
												 * mysterious.
												 */}
												<DropdownMenuItem
													disabled={
														inviting || c.status !== StatusEnum.Pending
													}
													onClick={(e) => {
														e.stopPropagation();
														onInvite(c);
													}}
												>
													<Send size={14} />
													{c.invitedAt
														? 'Re-send invitation'
														: 'Send invitation'}
												</DropdownMenuItem>
												<DropdownMenuItem
													onClick={(e) => {
														e.stopPropagation();
														onEdit(c);
													}}
												>
													<Pencil size={14} /> Edit
												</DropdownMenuItem>
												<DropdownMenuItem
													variant="destructive"
													onClick={(e) => {
														e.stopPropagation();
														onDelete(c);
													}}
												>
													<Trash2 size={14} /> Remove
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</div>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</Card>
	);
}
